import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable, tap } from 'rxjs';
import {
  AvailabilityResponse,
  BookingCreate,
  BookingOut,
  GuestToken,
  MealTypeOut,
  RoomTypeCreate,
  RoomTypeOut,
  VerificationPurpose,
  VerificationRequested,
  VerificationSendLog,
} from './models';

// 相對路徑：dev-server 會把 /api 與 /static 代理到後端（proxy.conf.js），
// 所以本機、tunnel、別人的機器打開都是同一組網址，不必改這裡。
export const API_BASE_URL = '';

const GUEST_TOKEN_KEY = 'booking_guest_token';

interface StoredGuestToken {
  access_token: string;
  email: string;
  expires_at: string;
}

@Injectable({ providedIn: 'root' })
export class ApiService {
  private guestToken: StoredGuestToken | null = null;

  constructor(private readonly http: HttpClient) {
    this.guestToken = this.readStoredToken();
  }

  listPublicRoomTypes(): Observable<RoomTypeOut[]> {
    return this.http.get<RoomTypeOut[]>(`${API_BASE_URL}/api/room-types/public`, {
      params: new HttpParams().set('only_active', 'true'),
    });
  }

  listPublicMealTypes(): Observable<MealTypeOut[]> {
    return this.http.get<MealTypeOut[]>(`${API_BASE_URL}/api/meal-types/public`, {
      params: new HttpParams().set('only_active', 'true'),
    });
  }

  searchAvailability(
    checkIn: string,
    checkOut: string,
    guests: number | null,
    mealTypeId: number | null,
    roomTypeId?: number,
  ): Observable<AvailabilityResponse> {
    let params = new HttpParams()
      .set('check_in', checkIn)
      .set('check_out', checkOut);
    if (mealTypeId !== null) {
      params = params.set('meal_type_id', String(mealTypeId));
    }
    if (guests !== null) {
      params = params.set('guests', String(guests));
    }
    if (roomTypeId !== undefined) {
      params = params.set('room_type_id', String(roomTypeId));
    }

    return this.http.get<AvailabilityResponse>(`${API_BASE_URL}/api/public/availability`, { params });
  }

  createBooking(payload: BookingCreate): Observable<BookingOut> {
    return this.http.post<BookingOut>(`${API_BASE_URL}/api/public/bookings`, payload);
  }

  listBookings(code?: string, email?: string): Observable<BookingOut[]> {
    let params = new HttpParams();
    if (code && code.trim()) {
      params = params.set('code', code.trim());
    }
    if (email && email.trim()) {
      params = params.set('email', email.trim());
    }
    return this.http.get<BookingOut[]>(`${API_BASE_URL}/api/bookings`, { params });
  }

  getBooking(code: string): Observable<BookingOut> {
    return this.http.get<BookingOut>(`${API_BASE_URL}/api/bookings/${code}`);
  }

  cancelBooking(code: string): Observable<BookingOut> {
    return this.http.post<BookingOut>(`${API_BASE_URL}/api/bookings/${code}/cancel`, {});
  }

  createRoomType(payload: RoomTypeCreate): Observable<RoomTypeOut> {
    return this.http.post<RoomTypeOut>(`${API_BASE_URL}/api/room-types`, payload);
  }

  // ---- 訪客 Email 驗證 ----

  get guestEmail(): string {
    return this.guestToken?.email ?? '';
  }

  hasGuestToken(): boolean {
    const token = this.guestToken;
    if (!token) return false;
    const expiresAt = Date.parse(token.expires_at);
    if (!Number.isNaN(expiresAt) && expiresAt <= Date.now()) {
      this.clearGuestToken();
      return false;
    }
    return true;
  }

  clearGuestToken(): void {
    this.guestToken = null;
    try {
      sessionStorage.removeItem(GUEST_TOKEN_KEY);
    } catch {
      // sessionStorage 可能被瀏覽器擋掉（無痕、第三方 cookie 設定），忽略即可。
    }
  }

  requestVerificationCode(email: string, purpose: VerificationPurpose): Observable<VerificationRequested> {
    return this.http.post<VerificationRequested>(`${API_BASE_URL}/api/public/verifications`, {
      email: email.trim(),
      purpose,
    });
  }

  // OpenAPI / Worker: GET /api/public/verifications/send-logs
  // /api/verifications/send-logs is not a route and 404s with {"detail":"Not Found"}.
  listVerificationSendLogs(): Observable<VerificationSendLog[]> {
    return this.http.get<VerificationSendLog[]>(`${API_BASE_URL}/api/public/verifications/send-logs`);
  }

  verifyCode(email: string, code: string, purpose: VerificationPurpose): Observable<GuestToken> {
    return this.http
      .post<GuestToken>(`${API_BASE_URL}/api/public/verifications/verify`, {
        email: email.trim(),
        code: code.trim(),
        purpose,
      })
      .pipe(tap((token) => this.storeToken(token)));
  }

  listMyBookings(): Observable<BookingOut[]> {
    return this.http.get<BookingOut[]>(`${API_BASE_URL}/api/public/my/bookings`, {
      headers: this.authHeaders(),
    });
  }

  getMyBooking(code: string): Observable<BookingOut> {
    return this.http.get<BookingOut>(`${API_BASE_URL}/api/public/my/bookings/${code}`, {
      headers: this.authHeaders(),
    });
  }

  cancelBookingWithVerification(
    bookingCode: string,
    email: string,
    verificationCode: string | null,
  ): Observable<BookingOut> {
    const body = {
      booking_code: bookingCode.trim(),
      email: email.trim(),
      verification_code: verificationCode ? verificationCode.trim() : null,
    };
    // 一次性驗證碼優先：使用者剛拿到 cancel 驗證碼時，可能同時握著另一個 Email 的
    // token，帶上 header 反而會讓後端用錯身分。沒有驗證碼時才用 token。
    const options = !body.verification_code && this.hasGuestToken() ? { headers: this.authHeaders() } : {};
    return this.http.post<BookingOut>(`${API_BASE_URL}/api/public/bookings/cancel`, body, options);
  }

  private authHeaders(): HttpHeaders {
    return new HttpHeaders().set('Authorization', `Bearer ${this.guestToken?.access_token ?? ''}`);
  }

  private storeToken(token: GuestToken): void {
    this.guestToken = {
      access_token: token.access_token,
      email: token.email,
      expires_at: token.expires_at,
    };
    try {
      sessionStorage.setItem(GUEST_TOKEN_KEY, JSON.stringify(this.guestToken));
    } catch {
      // 存不進去就只留在記憶體，重新整理後要再驗證一次。
    }
  }

  private readStoredToken(): StoredGuestToken | null {
    try {
      const raw = sessionStorage.getItem(GUEST_TOKEN_KEY);
      if (!raw) return null;
      const parsed = JSON.parse(raw) as StoredGuestToken;
      if (!parsed?.access_token || !parsed?.email) return null;
      return parsed;
    } catch {
      return null;
    }
  }
}
